import * as React from 'react';
import {
  Settings,
  Activity,
  ZoomIn,
  Languages,
  LogOut,
  Minus,
  Plus,
  Bug,
  X,
} from 'lucide-react';
import apiService from '../../services/api';
import * as tokenStorage from '../../utils/tokenStorage';
import {
  FONT_SCALE_MIN,
  FONT_SCALE_MAX,
  decreaseFontScale,
  getFontScale,
  getFontScaleLabel,
  increaseFontScale,
  isDebugLogPanelVisible,
  setDebugLogPanelVisible,
} from '../../utils/addInPreferences';
import { setRemoteDebugEnabled } from '../../utils/RemoteDebugLog';
import { webAppPathUrl } from '../../utils/webAppBaseUrl';
import { openExternalUrl } from '../../utils/openExternalUrl';
import './ProfileView.css';

interface UserInfo {
  id: string;
  email?: string;
  name?: string;
  full_name?: string;
}

interface ProfileViewProps {
  onClose: () => void;
  onSignOut: () => void;
  onPreferencesChange?: () => void;
}

const ADDIN_VERSION = '1.0.0';

function orgDisplayName(): string {
  const configured = (process.env.COURTYARD_ORG_NAME || '').trim();
  if (configured) return configured;
  return 'Courtyard';
}

function userInitial(email?: string, name?: string): string {
  const source = (name || email || '?').trim();
  return source.charAt(0).toUpperCase();
}

function parseStoredUser(): UserInfo | null {
  try {
    const raw = tokenStorage.getUser();
    return raw ? (JSON.parse(raw) as UserInfo) : null;
  } catch {
    return null;
  }
}

export const ProfileView: React.FC<ProfileViewProps> = ({ onClose, onSignOut, onPreferencesChange }) => {
  const [user, setUser] = React.useState<UserInfo | null>(parseStoredUser);
  const [loading, setLoading] = React.useState(!parseStoredUser());
  const [fontScale, setFontScaleState] = React.useState(getFontScale);
  const [debugLogPanelVisible, setDebugLogPanelVisibleState] = React.useState(isDebugLogPanelVisible);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await apiService.getCurrentUser();
        if (cancelled) return;
        if (response.success && response.data) {
          const userData: UserInfo = {
            id: response.data.id,
            email: response.data.email,
            name: response.data.name,
            full_name: response.data.full_name,
          };
          setUser(userData);
          tokenStorage.setUser(JSON.stringify(userData));
        }
      } catch (err) {
        console.warn('[ProfileView] Failed to load user:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const notifyPreferencesChange = React.useCallback(() => {
    onPreferencesChange?.();
  }, [onPreferencesChange]);

  const handleFontScaleDecrease = () => {
    const next = decreaseFontScale();
    setFontScaleState(next);
    notifyPreferencesChange();
  };

  const handleFontScaleIncrease = () => {
    const next = increaseFontScale();
    setFontScaleState(next);
    notifyPreferencesChange();
  };

  const handleDebugLogPanelToggle = (next: boolean) => {
    setDebugLogPanelVisible(next);
    setRemoteDebugEnabled(next);
    setDebugLogPanelVisibleState(next);
    notifyPreferencesChange();
  };

  const openExternal = (path: string) => {
    openExternalUrl(webAppPathUrl(path));
  };

  const subtitle = orgDisplayName();

  return (
    <div className="profile-view">
      <header className="profile-header">
        <div className="profile-header-start">
          <button
            type="button"
            className="profile-close-btn"
            onClick={onClose}
            aria-label="Close account"
          >
            <X size={18} />
          </button>
          <h1 className="profile-title">Account</h1>
        </div>
        <img
          className="profile-org-logo"
          src="/assets/Courtyard%20Logo%20-%20tight.png"
          alt="Courtyard"
        />
      </header>

      <section className="profile-user-card">
        <div className="profile-user-main">
          <div className="profile-avatar" aria-hidden>
            {userInitial(user?.email, user?.full_name || user?.name)}
          </div>
          <div className="profile-user-text">
            {loading && !user ? (
              <span className="profile-user-loading">Loading account…</span>
            ) : (
              <>
                <div className="profile-user-email">{user?.email || '—'}</div>
                <div className="profile-user-subtitle">{subtitle}</div>
              </>
            )}
          </div>
        </div>
      </section>

      <nav className="profile-menu" aria-label="Account options">
        <button
          type="button"
          className="profile-menu-item"
          onClick={() => openExternal('/account/settings')}
        >
          <Settings className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">Settings</span>
        </button>

        <button
          type="button"
          className="profile-menu-item"
          onClick={() => openExternal('/account/usage')}
        >
          <Activity className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">Usage</span>
        </button>

        <div className="profile-menu-item profile-menu-item-static">
          <ZoomIn className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">Screen resolution</span>
          <div className="profile-font-controls">
            <button
              type="button"
              className="profile-font-btn"
              onClick={handleFontScaleDecrease}
              disabled={fontScale <= FONT_SCALE_MIN}
              aria-label="Decrease screen resolution"
            >
              <Minus size={14} />
            </button>
            <span className="profile-font-label">{getFontScaleLabel(fontScale)}</span>
            <button
              type="button"
              className="profile-font-btn"
              onClick={handleFontScaleIncrease}
              disabled={fontScale >= FONT_SCALE_MAX}
              aria-label="Increase screen resolution"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className="profile-menu-item profile-menu-item-static">
          <Languages className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">Language</span>
          <span className="profile-menu-value">ENGLISH USA</span>
        </div>

        <div className="profile-menu-item profile-menu-item-static">
          <Bug className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">Debug logging</span>
          <label className="profile-toggle">
            <input
              type="checkbox"
              checked={debugLogPanelVisible}
              onChange={(e) => handleDebugLogPanelToggle(e.target.checked)}
              aria-label="Show debug logging panel for submit and paragraph sync issues"
            />
            <span className="profile-toggle-track" aria-hidden />
          </label>
        </div>

        <button type="button" className="profile-menu-item profile-menu-item-signout" onClick={onSignOut}>
          <LogOut className="profile-menu-icon" size={18} />
          <span className="profile-menu-label">Sign out</span>
        </button>
      </nav>

      <footer className="profile-footer">
        <div className="profile-footer-meta">
          <span>Courtyard Word {ADDIN_VERSION}</span>
          <span>© All Rights Reserved</span>
        </div>
      </footer>
    </div>
  );
};
